/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { isNil, isEqual, isEmpty } from "lodash";
import { BarChartMarshalledGroup, BarChartMergedData } from "@evergis/charts";
import { IconButton, ITheme, ThemeProvider } from "@evergis/ui";

import { ChartWrapper } from "../FeatureCardChart/ChartWrapper";
import { BarChart, TooltipContainer } from "../../../../components/StatisticsCharts/styled";
import {
  BarChartFilterArrows,
  BarChartContainer,
  BarChartFilterHeader,
  BarChartFilterSelected,
  BarChartWrapper,
} from "./styled";
import {
  BAR_CHART_FOOTER_MARGIN,
  BAR_CHART_HEADER_HEIGHT,
  DEFAULT_BAR_CHART_HEIGHT,
  DEFAULT_BAR_CHART_MARGINS,
  DEFAULT_BAR_PADDING,
  DEFAULT_BAR_WIDTH,
} from "./constants";
import { DEFAULT_BARCHART_RADIUS } from "../../constants";
import { getConfigFilter } from "../../utils/getConfigFilter";
import { getDataFromRelatedFeatures } from "../../utils/getDataFromRelatedFeatures";
import { getColorsFromFilterItems } from "../FeatureCardChart/getColorsFromFilterItems";
import { getMinValueFromFilterItems } from "../FeatureCardChart/getMinValueFromFilterItems";
import { getDataFromFilterItems } from "../FeatureCardChart/getDataFromFilterItems";
import { getChartMarkers } from "../../utils/getChartMarkers";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { useFeatureFilters } from "../../hooks/useFeatureFilters";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { WidgetFilterProps } from "./types";
import { FilterItem } from "../../types";

function getIndexRange(
  selectedPixels: [number, number] | null,
  amount: number,
  barFullWidth = DEFAULT_BAR_WIDTH + DEFAULT_BAR_PADDING,
): [number, number] | null {
  if (!selectedPixels || !amount) {
    return null;
  }

  const [startPixel, endPixel] = selectedPixels;
  const startIndex = Math.floor(startPixel / barFullWidth);
  const endIndex = Math.floor(endPixel / barFullWidth);

  if (startIndex > endIndex || endIndex < 0 || startIndex >= amount) {
    return null;
  }

  return [startIndex, endIndex];
}

function getPixelRange(
  selectedValues: [number, number] | null,
  filterItems: FilterItem[],
  barFullWidth = DEFAULT_BAR_WIDTH + DEFAULT_BAR_PADDING,
): [number, number] | null {
  if (!selectedValues || !filterItems?.length) {
    return null;
  }

  const [startValue, endValue] = Array.isArray(selectedValues) ? selectedValues : [selectedValues, selectedValues];
  const startIndex = filterItems.findIndex(({ name }) => name?.toString() === startValue?.toString());
  const endIndex = filterItems.findIndex(({ name }) => name?.toString() === endValue?.toString());

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  return [startIndex * barFullWidth, endIndex * barFullWidth];
}

export const useBarChartFilter = ({ type, filter, config, elementConfig }: WidgetFilterProps) => {
  const theme = useTheme() as ITheme;
  const { loading: isLoading, dataSources, filters, changeFilters } = useWidgetContext(type);
  const [selectedPixels, setSelectedPixels] = useState<[number, number] | null>(null);

  const { filterName } = filter.options;
  const { options } = elementConfig;
  const {
    barHeight,
    barWidth = DEFAULT_BAR_WIDTH,
    padding = DEFAULT_BAR_PADDING,
    height = DEFAULT_BAR_CHART_HEIGHT,
    radius = DEFAULT_BARCHART_RADIUS,
    width: configWidth,
    markers: configMarkers,
    colors: configColors,
    colorAttribute,
    defaultColor,
    primaryColor,
    drawMinMax,
  } = options || {};
  const barFullWidth = barWidth + padding;
  const { filters: configFilters } = config;

  const configFilter = useMemo(() => getConfigFilter(filterName, configFilters), [configFilters, filterName]);

  const { relatedDataSource, attributeValue, defaultValue } = configFilter || {};

  const dataSource = useMemo(
    () => dataSources?.find(({ name }) => name === relatedDataSource),
    [dataSources, relatedDataSource],
  );

  const filterItems = useMemo(
    () =>
      getDataFromRelatedFeatures({
        dataSource,
        config: elementConfig,
        filters: config?.filters,
        relatedConfig: {
          dataSourceName: configFilter.relatedDataSource,
          attributeName: configFilter.attributeValue,
          attributeTitle: configFilter.attributeAlias,
          attributeColor: colorAttribute,
        },
        layerInfo: { layerDefinition: { attributes: dataSource?.attributeDefinition } } as LayerInfo,
      }) as FilterItem[],
    [
      colorAttribute,
      config?.filters,
      configFilter.attributeAlias,
      configFilter.attributeValue,
      configFilter.relatedDataSource,
      dataSource,
      elementConfig,
    ],
  );

  const amount = useMemo(() => filterItems?.length || 0, [filterItems?.length]);
  const width = useMemo(() => configWidth || amount * barFullWidth, [amount, barFullWidth, configWidth]);
  const data = useMemo(() => getDataFromFilterItems(filterItems), [filterItems]);

  const loading = isLoading && !amount;

  const { formatFilterColor, onFilter } = useFeatureFilters(type, filterName, filterItems);

  const colors = useMemo(
    () => getColorsFromFilterItems(filterItems, defaultColor, formatFilterColor),
    [defaultColor, filterItems, formatFilterColor],
  );

  const selectedRange = useMemo(
    () => getIndexRange(selectedPixels, amount, barFullWidth),
    [amount, barFullWidth, selectedPixels],
  );

  const customXAxis = useCallback(
    xAxis => {
      xAxis.select(".tick:first-of-type").attr("transform", "translate(0, 0)");
      xAxis.select(".tick:last-of-type").attr("transform", `translate(${width}, 0)`);
    },
    [width],
  );

  const customXAxisBottom = useCallback(
    xAxisBottom => {
      xAxisBottom.tickFormat((index: number) =>
        (drawMinMax === undefined || drawMinMax) && (index === 0 || index === data.length - 1)
          ? data[index].groupName
          : "",
      );
    },
    [data, drawMinMax],
  );

  const customYAxis = useCallback(yAxis => {
    yAxis.remove();
  }, []);

  const customBars = useCallback(
    ({ bars }) => {
      bars.attr("rx", radius).attr("ry", radius);
    },
    [radius],
  );

  const tooltipY = useMemo(
    () => (barHeight ? height - barHeight - DEFAULT_BAR_CHART_MARGINS.bottom : undefined),
    [barHeight, height],
  );

  const markers = useMemo(() => getChartMarkers(filterItems, configMarkers), [configMarkers, filterItems]);

  const hasDefinedColors = useMemo(() => configColors?.length || colorAttribute, [configColors, colorAttribute]);

  const marshalledMap = useCallback(
    items => {
      if (hasDefinedColors) return undefined;

      return items.map(([item]: BarChartMarshalledGroup[], index: number) => {
        return [
          {
            ...item,
            color:
              selectedRange && index >= selectedRange[0] && index <= selectedRange[1]
                ? primaryColor || theme.palette.primary
                : defaultColor || theme.palette.elementDeep,
          },
        ];
      });
    },
    [defaultColor, hasDefinedColors, primaryColor, selectedRange, theme.palette.elementDeep, theme.palette.primary],
  );

  const minValue = useMemo(() => getMinValueFromFilterItems(filterItems), [filterItems]);

  const onBarClick = useCallback(
    (group: BarChartMarshalledGroup) => {
      if (loading) return;

      setSelectedPixels([group.groupIndex * barFullWidth, group.groupIndex * barFullWidth]);

      const feature = filterItems.find(({ value }) => value === group.value);

      onFilter(feature.name);
    },
    [barFullWidth, filterItems, loading, onFilter],
  );

  const getValueRange = useCallback(
    (pixelRange?: [number, number]) => {
      const currentRange = pixelRange || selectedPixels;
      const indexRange = getIndexRange(currentRange, filterItems?.length, barFullWidth);

      if (!indexRange || isNil(filterItems[indexRange[0]]) || isNil(filterItems[indexRange[1]])) return null;

      return [filterItems[indexRange[0]].name, filterItems[indexRange[1]].name];
    },
    [barFullWidth, filterItems, selectedPixels],
  );

  const onSelect = useCallback(
    (pixelRange: [number, number]) => {
      const valueRange = getValueRange(pixelRange);

      if (!valueRange) return;

      setSelectedPixels(pixelRange);
      changeFilters(
        {
          [filterName]: {
            value: valueRange,
            min: valueRange[0],
            max: valueRange[1],
          },
        },
        configFilter?.resetFilters,
      );
    },
    [changeFilters, configFilter?.resetFilters, filterName, getValueRange],
  );

  const prevDisabled = useMemo(
    () => loading || !selectedPixels || selectedPixels?.[0] - barFullWidth < 0,
    [barFullWidth, loading, selectedPixels],
  );

  const nextDisabled = useMemo(
    () => loading || !selectedPixels || selectedPixels?.[1] + barFullWidth >= width,
    [barFullWidth, loading, selectedPixels, width],
  );

  const onPrev = useCallback(() => {
    if (prevDisabled) return;

    onSelect([selectedPixels[0] - barFullWidth, selectedPixels[1] - barFullWidth]);
  }, [barFullWidth, prevDisabled, onSelect, selectedPixels]);

  const onNext = useCallback(() => {
    if (nextDisabled) return;

    onSelect([selectedPixels[0] + barFullWidth, selectedPixels[1] + barFullWidth]);
  }, [barFullWidth, nextDisabled, onSelect, selectedPixels]);

  useEffect(() => {
    if (selectedRange || !defaultValue || !filterItems?.length) return;

    const pixelRange = getPixelRange(
      (Array.isArray(defaultValue) ? defaultValue : [defaultValue, defaultValue]) as [number, number],
      filterItems,
      barFullWidth,
    );

    onSelect(pixelRange);
  }, [amount]);

  useEffect(() => {
    if (isEmpty(filters[filterName]?.value)) {
      if (!isEmpty(selectedPixels)) {
        setSelectedPixels(null);
      }

      return;
    }

    if (selectedRange || !amount || isEqual(filters[filterName]?.value, getValueRange())) return;

    const pixelRange = getPixelRange(filters[filterName].value as [number, number], filterItems, barFullWidth);

    setSelectedPixels(pixelRange);
  }, [filters[filterName]?.value]);

  return {
    data,
    colors,
    configFilters,
    width,
    height,
    markers,
    minValue,
    tooltipY,
    attributeValue,
    loading,
    prevDisabled,
    nextDisabled,
    selectedRange,
    barWidth,
    barHeight,
    padding,
    customXAxis,
    customXAxisBottom,
    customYAxis,
    customBars,
    marshalledMap: hasDefinedColors ? undefined : marshalledMap,
    onPrev,
    onNext,
    onSelect,
    onBarClick,
  };
};

export const BarChartFilter: FC<WidgetFilterProps> = ({ type, config, elementConfig, filter }) => {
  const { style } = filter;
  const {
    configFilters,
    loading,
    height,
    data,
    barWidth,
    barHeight,
    attributeValue,
    padding,
    selectedRange,
    prevDisabled,
    nextDisabled,
    onPrev,
    onNext,
    ...rest
  } = useBarChartFilter({
    type,
    config,
    elementConfig,
    filter,
  });

  const renderSelectedRange = useMemo(() => {
    if (!selectedRange) return null;

    return (
      <BarChartFilterSelected>
        {selectedRange[0] === selectedRange[1]
          ? data[selectedRange[0]].groupName
          : `${data[selectedRange[0]].groupName} - ${data[selectedRange[1]].groupName}`}
      </BarChartFilterSelected>
    );
  }, [data, selectedRange]);

  const renderTooltip = useCallback((groups: BarChartMergedData) => {
    const group = groups[0];

    return (
      <ThemeProvider>
        <TooltipContainer>
          {group.groupName === group.value.toString() ? group.value : [group.groupName, group.value].join(": ")}
        </TooltipContainer>
      </ThemeProvider>
    );
  }, []);

  const totalHeight = height + BAR_CHART_HEADER_HEIGHT + BAR_CHART_FOOTER_MARGIN;

  return (
    <ChartWrapper height={totalHeight} loading={loading} style={style}>
      <BarChartWrapper height={totalHeight}>
        <BarChartFilterHeader height={BAR_CHART_HEADER_HEIGHT}>
          <BarChartFilterArrows>
            <IconButton kind="prev" disabled={prevDisabled} onClick={onPrev} />
            <IconButton kind="next" disabled={nextDisabled} onClick={onNext} />
          </BarChartFilterArrows>
          {renderSelectedRange}
        </BarChartFilterHeader>
        <BarChartContainer barHeight={barHeight} marginBottom={DEFAULT_BAR_CHART_MARGINS.bottom}>
          <BarChart
            data={data}
            margin={DEFAULT_BAR_CHART_MARGINS}
            height={height}
            barWidth={barWidth}
            barPadding={padding}
            xAxisPadding={0}
            yAxisPadding={0}
            selectable={!loading}
            dynamicTooltipEnable
            isBarTooltip
            renderTooltip={renderTooltip}
            {...rest}
          />
        </BarChartContainer>
      </BarChartWrapper>
    </ChartWrapper>
  );
};
